import { getGetChannelsApiV1QueryKey } from "@/api/__generated__/api.schema";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useIsMobile } from "@/hooks/use-mobile";
import { NewTrackingEvent, TrackingEvent, trackEvent, trackNewEvent } from "@/lib/track/track-event";
import { RegisterChannelFlow } from "@/routes/channels/components/register-channel-flow";
import { Button, Typography, useFullScreenOverlay, useModal } from "@backpac/monica";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

export function useChannelRegisterFlow() {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  const { open, close } = useModal();
  const { open: openModal, close: closeModal } = useModal();
  const { open: openFullScreenOverlay, close: closeFullScreenOverlay } = useFullScreenOverlay();

  const handleSuccess = useCallback(
    async (channelId: string) => {
      await queryClient.invalidateQueries({
        queryKey: [...getGetChannelsApiV1QueryKey(workspaceId)],
      });
      trackEvent(TrackingEvent.ChannelAdded, {
        channel_id: channelId,
        is_success: true,
      });
      trackNewEvent(NewTrackingEvent.CompleteChannelVerification, {
        channel_handle: channelId,
        register_options: "channel URL",
      });

      open({
        title: "",
        className: "max-w-[320px] desktop:max-w-[480px] p-6 desktop:px-[32px] desktop:py-[32px]",
        withNoHeader: true,
        hideCloseButton: true,
        children: (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2 text-center">
              <Typography size="2xl">✅</Typography>
              <Typography font="unbounded" size="lg" color="textPrimary">
                You're channel has been verified
              </Typography>
              <Typography size="paragraphXs" color="textMuted">
                You can now delete the verification code from the description. We'll review your channel for compliance
                with our internal policies, which may take up to 24 hours.
              </Typography>
            </div>
            <Button className="w-full" size="lg" onClick={close}>
              Confirm
            </Button>
          </div>
        ),
      });
    },
    [close, open, queryClient, workspaceId],
  );

  const handleRegisterChannel = useCallback(
    async (options?: { defaultStep?: number; channelUrl: string; channelId: string }) => {
      if (isMobile) {
        openFullScreenOverlay({
          title: "",
          className: "min-h-screen",
          withNoHeader: true,
          onClose: async () => {
            await queryClient.invalidateQueries({
              queryKey: [...getGetChannelsApiV1QueryKey(workspaceId)],
            });
          },
          children: (
            <RegisterChannelFlow
              defaultChannel={{
                url: options?.channelUrl ?? "",
                id: options?.channelId ?? "",
              }}
              defaultStep={options?.defaultStep}
              onClose={closeFullScreenOverlay}
              onSuccess={(channelId) => {
                closeFullScreenOverlay();
                handleSuccess(channelId);
              }}
            />
          ),
        });
      } else {
        openModal({
          title: "",
          className: "max-w-[880px] p-0 gap-0 min-h-[520px] bg-transparent",
          onClose: async () => {
            await queryClient.invalidateQueries({
              queryKey: [...getGetChannelsApiV1QueryKey(workspaceId)],
            });
          },
          children: (
            <RegisterChannelFlow
              defaultChannel={{
                url: options?.channelUrl ?? "",
                id: options?.channelId ?? "",
              }}
              defaultStep={options?.defaultStep}
              onClose={closeModal}
              onSuccess={(channelId) => {
                closeModal();
                handleSuccess(channelId);
              }}
            />
          ),
        });
      }
      trackNewEvent(NewTrackingEvent.ViewAddOptionModal, {});
    },
    [
      closeFullScreenOverlay,
      closeModal,
      handleSuccess,
      isMobile,
      openFullScreenOverlay,
      openModal,
      queryClient,
      workspaceId,
    ],
  );

  return { open: handleRegisterChannel };
}
